import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// import { Link } from "react-router-dom";
import { useParams, useHistory } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import * as Tooltip from '@radix-ui/react-tooltip';

import { toggleDiagramDetails, toggleModalLanguages, toggleDiagramCreateNew, toggleModalDiagramClone, togglePricingPlanUpgrade } from '../../../redux/main/main.actions';
import { toggleModalDiagramExportSQL, enableExportImageClick } from '../../../redux/diagram/diagram.actions';

// eslint-disable-next-line
import {
    updateScaleToFit,
    toggleBtnSearch,
    toggleBtnLeftPanel,
    toggleBtnRedGreenView,
    toggleBtnViewModes,
    clickEscape,
    setElementsViewMode,
    setNewPenColor,
    setNewPenWidth,
    clearNewPen,
    toggleBtnExport,
    toggleBtnExportImage,
    toggleBtnExportCode,
    toggleBtnSqlSyntax,
    setSqlSyntax,
    toggleBtnSettings,
    toggleBtnNewDiagram,
    toggleBtnModalShortcuts,
    toggleBtnGridStyle,
} from '../../../redux/diagram/diagram.actions';
import {
    // CONTROLS: LEFT PANEL | SEARCH | EXPORT
    selectIsBtnSettingsEnabled,
    selectIsBtnNewDiagramEnabled,


    // list of controls to disable shortcuts
    selectIsBtnRedGreenViewEnabled,
    selectIsBtnLeftPanelEnabled,
    selectIsBtnSearchEnabled,
    selectIsBtnExportImageEnabled,
    // selectIsBtnExportCodeEnabled,

    selectIsBtnExportEnabled,
    selectIsBtnExportCodeEnabled,

    selectIsBtnSqlSyntaxEnabled,


    // CONTROLS: PEN
    selectIsBtnPenEnabled,

    // CONTROLS: VIEW MODES
    selectIsBtnViewModesEnabled,

    selectIsBtnPopupNewsEnabled,

    // CONTROLS: PRS | VIEW MODES
    selectViewMode,
    selectFocusedInsideElement,

    selectIsEnabledElementDescriptionEditing,

    selectIsBtnGridStyleEnabled,
    selectDiagramIsArchived,
    selectIsControlsFocusMode,
    selectIsTopControlsHidden,
} from '../../../redux/diagram/diagram.selectors';

import { useMeta } from '../../../lib/yjs';

import {
    selectIsModalSupportEnabled,
    selectIsModalDiagramDetailsEnabled,
    selectIsModalDiagramShareEnabled,
    selectDiagramTitle,
} from '../../../redux/main/main.selectors';


// eslint-disable-next-line
import {
    BackgroundColorCircleBigIcon,
    IconBack,
    // LeftPanelOpenIcon,
    AutoLayoutIcon,
    ChangeRequestIcon,
    ExportIcon,
    HistoryVersionsIcon,
    // LeftSidebarOpenedIcon,
    LeftSidebarClosedIcon,
    EditorIcon,
    FitToZoomIcon,
    SearchIcon,
    ViewModeIcon,
    LogoIcon,
    GridIconNew,
    OkCheckIcon,
} from '../../Icon';

import { LANGUAGES } from '../../../i18n/menuLanguages';

import { selectDiagramPrefs } from '../../../redux/preferences/preferences.selectors';
import { preferencesPatchRequest } from '../../../redux/preferences/preferences.actions';
import { trackCanvasEvent } from '../../../lib/analytics';

import { useShortKeysNavigation } from '../../../hooks/useShortKeysNavigation';
import { useDeviceType } from '../../../hooks/useDeviceType.ts';

import './ControlsTopLeft.scss';


const CtlSwitch = ({ on }) => (
    <span className={`ctl-switch ${on ? 'on' : ''}`}>
        <span className="ctl-switch-knob" />
    </span>
);

// Brand names (MySQL, PostgreSQL, ...) are proper nouns and stay untranslated;
// only the two generic fallback labels are looked up via `t`.
function buildDbEngineName(value = '', t) {
    switch (value) {
        case 'basicsql':
            return t('db_engine.basic');
        case 'mysql':
            return 'MySQL';
        case 'postgresql':
            return 'PostgreSQL';
        case 'sqlserver':
            return 'SQL Server';
        case 'oracle':
            return 'Oracle';
        case 'sqlite':
            return 'SQLite';
        case 'mariadb':
            return 'MariaDB';
        default:
            return t('db_engine.general');
    }
}

export const ControlsTopLeft = () => {
    const history = useHistory();
    const { diagramId } = useParams();  // отримаємо ID діаграми з URL

    const { isDesktop, isDesktop1350 } = useDeviceType(); // mobile/tablet => false

    const { t, i18n } = useTranslation();

    const { map: shortKeysMap, isMac } = useShortKeysNavigation();

    const handleClick = () => {
        setTimeout(() => {
            history.push(`/`);
        }, 100);
    };

    const dispatch = useDispatch();
    // eslint-disable-next-line
    const isBtnRedGreenViewEnabled = useSelector(
        selectIsBtnRedGreenViewEnabled
    );

    const isBtnSearchEnabled = useSelector(selectIsBtnSearchEnabled);
    const isControlsFocusMode = useSelector(selectIsControlsFocusMode);
    const isTopControlsHidden = useSelector(selectIsTopControlsHidden);
    const topHidden = isControlsFocusMode && isTopControlsHidden;
    const isBtnLeftPanelEnabled = useSelector(selectIsBtnLeftPanelEnabled);

    const isModalSupportEnabled = useSelector(selectIsModalSupportEnabled);
    const isModalDiagramDetailsEnabled = useSelector(selectIsModalDiagramDetailsEnabled);
    const isModalDiagramShareEnabled = useSelector(selectIsModalDiagramShareEnabled);


    const isBtnPopupNewsEnabled = useSelector(selectIsBtnPopupNewsEnabled);

    const meta = useMeta();
    const diagramDbEngine = meta.dbEngine;
    const isArchived = useSelector(selectDiagramIsArchived);
    const diagramTitle = useSelector(selectDiagramTitle);


    const isBtnViewModesEnabled = useSelector(selectIsBtnViewModesEnabled);
    const viewMode = useSelector(selectViewMode);
    const viewModesBtnRef = useRef(null);
    const viewModesMenuRef = useRef(null);

    const isBtnPenEnabled = useSelector(selectIsBtnPenEnabled);

    const isBtnExportEnabled = useSelector(selectIsBtnExportEnabled);
    const exportBtnRef = useRef(null);
    const exportMenuRef = useRef(null);

    const isBtnExportImageEnabled = useSelector(selectIsBtnExportImageEnabled);
    const exportImageMenuRef = useRef(null);

    const isBtnExportCodeEnabled = useSelector(selectIsBtnExportCodeEnabled);
    const exportCodeMenuRef = useRef(null);
    const openExportCodeTimerRef = useRef(null);

    const cancelOpenExportCodeTimer = () => {
        if (openExportCodeTimerRef.current) {
            clearTimeout(openExportCodeTimerRef.current);
            openExportCodeTimerRef.current = null;
        }
    };

    useEffect(() => () => cancelOpenExportCodeTimer(), []);

    const isBtnSqlSyntaxEnabled = useSelector(selectIsBtnSqlSyntaxEnabled);
    // const sqlSyntax = useSelector(selectSqlSyntax);
    const sqlSyntaxBtnRef = useRef(null);
    const sqlSyntaxMenuRef = useRef(null);

    const isBtnSettingsEnabled = useSelector(selectIsBtnSettingsEnabled);
    const settingsBtnRef = useRef(null);
    const settingsMenuRef = useRef(null);
    // View/Language sub-menu triggers — used to anchor the hoisted flyouts
    // (see openSettingsSubMenu render below) via offsetTop against
    // settingsMenuRef, their offsetParent.
    const viewTriggerBtnRef = useRef(null);
    const languageTriggerBtnRef = useRef(null);

    const isBtnNewDiagramEnabled = useSelector(selectIsBtnNewDiagramEnabled);
    const newDiagramBtnRef = useRef(null);
    const newDiagramMenuRef = useRef(null);

    // settings sub-menus (view | language) — profile-menu hover/click pattern
    const [openSettingsSubMenu, setOpenSettingsSubMenu] = useState(null); // null | 'view' | 'language'
    const openSubMenuTimerRef = useRef(null);

    const cancelOpenSubMenuTimer = () => {
        if (openSubMenuTimerRef.current) {
            clearTimeout(openSubMenuTimerRef.current);
            openSubMenuTimerRef.current = null;
        }
    };

    useEffect(() => () => cancelOpenSubMenuTimer(), []);

    // closing the settings menu (Escape, other control, outside click) resets sub-menus
    useEffect(() => {
        if (!isBtnSettingsEnabled) {
            cancelOpenSubMenuTimer();
            setOpenSettingsSubMenu(null);
        }
    }, [isBtnSettingsEnabled]);

    // Hovering any settings item other than a sub-menu trigger closes the open
    // sub-menu; hovers inside a sub-menu are ignored. Delegated on the container
    // so every item (incl. disabled ones) is covered without per-button handlers.
    const handleSettingsMenuMouseOver = (e) => {
        if (e.target.closest?.('.settings-sub-menu')) return;
        const item = e.target.closest?.('.control-button');
        if (!item) return;
        if (item.classList.contains('view-trigger') || item.classList.contains('language-trigger')) return;
        cancelOpenSubMenuTimer();
        setOpenSettingsSubMenu(null);
    };

    const diagramPrefs = useSelector(selectDiagramPrefs);

    // third-level flyouts inside the View sub-menu — same open pattern as
    // openSettingsSubMenu (150ms hover intent, click opens immediately)
    const [openViewFlyout, setOpenViewFlyout] = useState(null); // null | 'input_mode' | 'background' | 'grid' | 'view_mode'
    const openFlyoutTimerRef = useRef(null);

    const cancelOpenFlyoutTimer = () => {
        if (openFlyoutTimerRef.current) {
            clearTimeout(openFlyoutTimerRef.current);
            openFlyoutTimerRef.current = null;
        }
    };

    useEffect(() => () => cancelOpenFlyoutTimer(), []);

    // leaving the View sub-menu (or closing settings) closes any flyout
    useEffect(() => {
        if (openSettingsSubMenu !== 'view') {
            cancelOpenFlyoutTimer();
            setOpenViewFlyout(null);
        }
    }, [openSettingsSubMenu]);

    const flyoutTriggerProps = (flyoutKey) => ({
        onMouseEnter: () => {
            if (openViewFlyout === flyoutKey) return;
            if (openViewFlyout) setOpenViewFlyout(null);
            cancelOpenFlyoutTimer();
            openFlyoutTimerRef.current = setTimeout(() => {
                openFlyoutTimerRef.current = null;
                setOpenViewFlyout(flyoutKey);
            }, 150);
        },
        onMouseLeave: cancelOpenFlyoutTimer,
        onClick: () => {
            cancelOpenFlyoutTimer();
            setOpenViewFlyout(flyoutKey);
        },
    });

    const closeViewFlyout = () => {
        cancelOpenFlyoutTimer();
        setOpenViewFlyout(null);
    };

    const toggleDiagramPref = (key) => {
        dispatch(preferencesPatchRequest({ diagram: { [key]: !diagramPrefs[key] } }));
    };

    const renderViewFlyout = (flyoutKey, options, currentValue, onPick) => {
        if (openViewFlyout !== flyoutKey) return null;
        return (
            <div className={`settings-sub-menu settings-flyout ${flyoutKey}`}>
                <div className="settings-sub-menu-list">
                    {options.map((opt) => (
                        <div className="control" key={String(opt.value)}>
                            <button
                                className={`control-button ${opt.value === currentValue ? 'selected' : ''}`}
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onPick(opt.value);
                                }}
                            >
                                <span className="settings-sub-menu-name">{t(`dropdown_ctrl_settings.${opt.labelKey}`)}</span>
                                {opt.value === currentValue && <OkCheckIcon />}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    };


    // Grid lives only in preferences (editor.grid: 'dots' | 'lines' | 'none').
    // null keeps the legacy "no grid" shape the render code expects. The wire
    // value is the string 'none' (not null — the backend's JSON_MERGE_PATCH
    // would treat a null value as "delete the key").
    const gridStyle = diagramPrefs?.grid === 'none' ? null : (diagramPrefs?.grid ?? null);
    const patchGridPref = (value) => {
        // value: null | 'lines' | 'dots' (legacy shape); 'none' on the wire.
        trackCanvasEvent('app_canvas_grid_layout_change', { style: value });
        dispatch(preferencesPatchRequest({ diagram: { grid: value ?? 'none' } }));
    };
    const isBtnGridStyleEnabled = useSelector(selectIsBtnGridStyleEnabled);
    const gridStyleBtnRef = useRef(null);
    const gridStyleMenuRef = useRef(null);

    const focusedInsideElementRedux = useSelector(selectFocusedInsideElement);

    const isEnabledElementDescriptionEditing = useSelector(selectIsEnabledElementDescriptionEditing);

    // const [isTransparent, setIsTransparent] = useState(false);

    useEffect(() => {
        const handleKeyPress = (event) => {
            const isCtrl = isMac ? event.metaKey : event.ctrlKey;

            const isOpenTextEditing = focusedInsideElementRedux || isEnabledElementDescriptionEditing;
            const isTextEditing =
                isOpenTextEditing ||
                isModalDiagramDetailsEnabled ||
                isModalSupportEnabled ||
                isModalDiagramShareEnabled ||
                isBtnSearchEnabled ||
                isBtnExportImageEnabled;

            // Ignore events from disabled or read-only elements
            if (event.target.disabled || event.target.readOnly) return;

            if (isTextEditing) {
                // [Ctrl + F]
                if (
                    (event.code === 'KeyF' || event.code === 'KeyK') && isCtrl &&
                    !event.altKey &&
                    !event.shiftKey
                ) {
                    event.stopPropagation();
                    event.preventDefault();
                }

                // [ESC]
                else if (event.code === 'Escape') {
                    event.preventDefault();
                    // console.log('Controls Top Left 1 Escape')

                    dispatch(clickEscape());
                }
                // } else if (isBtnExportImageEnabled) {
                //     // [Ctrl + F]
                //     if (
                //         event.code === 'KeyF' && isCtrl &&
                //         !event.altKey &&
                //         !event.shiftKey
                //     ) {
                //         event.stopPropagation();
                //         event.preventDefault();
                //     }

                //     // [ E ]
                //     else if (
                //         event.code === 'KeyE' &&
                //         !event.ctrlKey &&
                //         !event.metaKey &&
                //         !event.altKey &&
                //         !event.shiftKey
                //     ) {
                //         event.preventDefault();
                //         // dispatch(toggleBtnExport());
                //         dispatch(toggleBtnExportImage());
                //     }

                //     // [ESC]
                //     else if (event.code === 'Escape') {
                //         event.preventDefault();
                //         // console.log('Controls Top Left 1 Escape')

                //         dispatch(clickEscape());
                //     }
            } else if (isBtnPenEnabled) {
                // -----------------------------------------------------------------------------
                // +/- [DELETE] = [CLEAR NEW DRAWING PEN]
                if (
                    event.code === 'Delete' &&
                    !event.ctrlKey &&
                    !event.metaKey &&
                    !event.shiftKey &&
                    !event.altKey
                ) {
                    event.preventDefault();
                    dispatch(clearNewPen());
                }
                // [CTRL + ENTER]
                if (
                    (event.code === 'Enter' || event.code === 'NumpadEnter') && isCtrl &&
                    !event.altKey &&
                    !event.shiftKey
                ) {
                    event.stopPropagation();
                    event.preventDefault();
                    dispatch(updateScaleToFit());
                }
                // [ESC]
                else if (event.code === 'Escape') {
                    event.preventDefault();
                    console.log('Controls Top Left 2 Escape')

                    dispatch(clickEscape());
                }
            } else {
                // // [ B ]
                // if (
                //     event.code === 'KeyB' &&
                //     !event.ctrlKey &&
                //     !event.metaKey &&
                //     !event.altKey &&
                //     !event.shiftKey
                // ) {
                //     event.preventDefault();
                //     dispatch(toggleBtnLeftPanel());

                //     // dispatch(handleVKey());
                // }
                // // [Ctrl + F] or [ F ] or [ S ]
                // else if (
                if (
                    (event.code === 'KeyF' || event.code === 'KeyK') && isCtrl &&
                    !event.altKey &&
                    !event.shiftKey
                ) {
                    event.stopPropagation();
                    event.preventDefault();
                    dispatch(toggleBtnSearch());
                }
                // [CTRL + ENTER]
                if (
                    (event.code === 'Enter' || event.code === 'NumpadEnter') && isCtrl &&
                    !event.altKey &&
                    !event.shiftKey
                ) {
                    event.stopPropagation();
                    event.preventDefault();
                    dispatch(updateScaleToFit());
                }
                // [ E ]
                else if (
                    event.code === 'KeyE' &&
                    !event.ctrlKey &&
                    !event.metaKey &&
                    !event.altKey &&
                    !event.shiftKey
                ) {
                    event.preventDefault();
                    // dispatch(toggleBtnExport());
                    dispatch(toggleBtnExportImage());
                }
                // // [ R ]
                // else if (
                //     event.code === 'KeyR' &&
                //     !event.ctrlKey &&
                //     !event.metaKey &&
                //     !event.altKey &&
                //     !event.shiftKey
                // ) {
                //     event.preventDefault();
                //     dispatch(toggleBtnRedGreenView());
                // }
                // [ESC]
                else if (event.code === 'Escape') {
                    event.preventDefault();
                    // console.log('Controls Top Left 3 Escape')

                    dispatch(clickEscape());
                }
            }
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [
        dispatch,
        isBtnPenEnabled,
        isModalSupportEnabled,
        isModalDiagramShareEnabled,
        isModalDiagramDetailsEnabled,
        focusedInsideElementRedux,
        isBtnLeftPanelEnabled,
        isBtnSearchEnabled,

        isBtnExportEnabled,
        isBtnExportImageEnabled,
        isBtnExportCodeEnabled,

        isBtnSqlSyntaxEnabled,

        isBtnRedGreenViewEnabled,
        isEnabledElementDescriptionEditing,

        isBtnSettingsEnabled,
    ]);

    // grid style menu
    const getGridStyleMenu = (isBtnGridStyleEnabled) => {
        if (!isBtnGridStyleEnabled) {
            return;
        }
        if (!gridStyleBtnRef.current) {
            return;
        }

        const rect = gridStyleBtnRef.current.getBoundingClientRect();
        let top = rect.bottom + window.scrollY;
        let left = rect.left + window.scrollX - 44.5 + 8 + 4;

        if (isDesktop1350) {
            left = left + 4;
            top = top - 1;
        }

        return (
            <div
                className={`view-modes-group grid-group`}
                ref={gridStyleMenuRef}
                style={{ top: `${top}px`, left: `${left}px` }}
            >
                <div className="controls-group">
                    <div className="control">
                        <button
                            className={`control-button control-button-option ${gridStyle === null ? 'selected' : ''}`}
                            type="button"
                            aria-pressed="false"
                            onClick={() => {
                                patchGridPref(null);
                                dispatch(toggleBtnGridStyle()); // close the menu (SET_GRID_STYLE used to)
                            }}
                        >
                            <svg className="icon-circle-none" width="17px" height="18px" viewBox="0 0 24 24" fill="none" style={{ width: '18px', marginLeft: '-4px', marginRight: '-2px' }}><g><path d="M12.5 2.2a10.3 10.3 0 1 0 10.3 10.3A10.299 10.299 0 0 0 12.5 2.2zm0 1a9.252 9.252 0 0 1 6.203 2.39L5.59 18.703A9.284 9.284 0 0 1 12.5 3.2zm0 18.6a9.252 9.252 0 0 1-6.203-2.39L19.41 6.297A9.284 9.284 0 0 1 12.5 21.8z"></path></g></svg>
                            {/* <svg className="icon-circle-none" width="18px" height="18px" viewBox="0 0 24 24" fill="none"><g><path d="M12.5 2.2a10.3 10.3 0 1 0 10.3 10.3A10.299 10.299 0 0 0 12.5 2.2zm0 1a9.252 9.252 0 0 1 6.203 2.39L5.59 18.703A9.284 9.284 0 0 1 12.5 3.2zm0 18.6a9.252 9.252 0 0 1-6.203-2.39L19.41 6.297A9.284 9.284 0 0 1 12.5 21.8z"></path></g></svg> */}
                            {t('dropdown_ctrl_grid.none')}
                        </button>
                    </div>

                    <div className="control control-m-t-1">
                        <button
                            className={`control-button control-button-option ${gridStyle === 'lines' ? 'selected' : ''}`}
                            type="button"
                            aria-pressed="false"
                            onClick={() => {
                                patchGridPref('lines');
                                dispatch(toggleBtnGridStyle()); // close the menu (SET_GRID_STYLE used to)
                            }}
                        >
                            {/* <svg className="icon-grid-new" width="18px" height="18px" viewBox="-0.25 0 24 24" stroke="#d3d4d7" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" fill="none"><g><path d="M7 3V21"></path><path d="M17 3V21"></path><path d="M21 17L3 17"></path><path d="M21 7L3 7"></path></g></svg> */}
                            <svg width="19px" height="19px" viewBox="1 0 48 48" fill="#000000" style={{ marginRight: '-2px', marginLeft: '-3px' }}><g> <path d="M44,26a2,2,0,0,0,0-4H38V14h6a2,2,0,0,0,0-4H38V4a2,2,0,0,0-4,0v6H26V4a2,2,0,0,0-4,0v6H14V4a2,2,0,0,0-4,0v6H4a2,2,0,0,0,0,4h6v8H4a2,2,0,0,0,0,4h6v8H4a2,2,0,0,0,0,4h6v6a2,2,0,0,0,4,0V38h8v6a2,2,0,0,0,4,0V38h8v6a2,2,0,0,0,4,0V38h6a2,2,0,0,0,0-4H38V26ZM34,14v8H26V14ZM14,14h8v8H14Zm0,20V26h8v8Zm20,0H26V26h8Z" fill="currentColor" stroke="black" strokeWidth="1.2px"></path></g></svg>
                            {t('dropdown_ctrl_grid.lines')}
                        </button>
                    </div>

                    <div className="control control-m-t-1">
                        <button
                            className={`control-button control-button-option ${gridStyle === 'dots' ? 'selected' : ''}`}
                            type="button"
                            aria-pressed="false"
                            onClick={() => {
                                patchGridPref('dots');
                                dispatch(toggleBtnGridStyle()); // close the menu (SET_GRID_STYLE used to)
                            }}
                        >
                            {/* <svg className="icon-grid-new" width="18px" height="18px" viewBox="-0.25 0 24 24" stroke="#d3d4d7" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" fill="none"><g><path d="M7 3V21"></path><path d="M17 3V21"></path><path d="M21 17L3 17"></path><path d="M21 7L3 7"></path></g></svg> */}
                            <svg width="20px" height="20px" viewBox="0.5 0 24 24" style={{ marginRight: '-3px', marginLeft: '-3px' }}>
                                <g>
                                    <path d="M8 6C8 7.10457 7.10457 8 6 8C4.89543 8 4 7.10457 4 6C4 4.89543 4.89543 4 6 4C7.10457 4 8 4.89543 8 6Z" fill="currentColor" stroke="black" strokeWidth="0.5px"></path>
                                    <path d="M8 12C8 13.1046 7.10457 14 6 14C4.89543 14 4 13.1046 4 12C4 10.8954 4.89543 10 6 10C7.10457 10 8 10.8954 8 12Z" fill="currentColor" stroke="black" strokeWidth="0.5px"></path>
                                    <path d="M6 20C7.10457 20 8 19.1046 8 18C8 16.8954 7.10457 16 6 16C4.89543 16 4 16.8954 4 18C4 19.1046 4.89543 20 6 20Z" fill="currentColor" stroke="black" strokeWidth="0.5px"></path>
                                    <path d="M14 6C14 7.10457 13.1046 8 12 8C10.8954 8 10 7.10457 10 6C10 4.89543 10.8954 4 12 4C13.1046 4 14 4.89543 14 6Z" fill="currentColor" stroke="black" strokeWidth="0.5px"></path>
                                    <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" fill="currentColor" stroke="black" strokeWidth="0.5px"></path>
                                    <path d="M14 18C14 19.1046 13.1046 20 12 20C10.8954 20 10 19.1046 10 18C10 16.8954 10.8954 16 12 16C13.1046 16 14 16.8954 14 18Z" fill="currentColor" stroke="black" strokeWidth="0.5px"></path>
                                    <path d="M18 8C19.1046 8 20 7.10457 20 6C20 4.89543 19.1046 4 18 4C16.8954 4 16 4.89543 16 6C16 7.10457 16.8954 8 18 8Z" fill="currentColor" stroke="black" strokeWidth="0.5px"></path>
                                    <path d="M20 12C20 13.1046 19.1046 14 18 14C16.8954 14 16 13.1046 16 12C16 10.8954 16.8954 10 18 10C19.1046 10 20 10.8954 20 12Z" fill="currentColor" stroke="black" strokeWidth="0.5px"></path>
                                    <path d="M18 20C19.1046 20 20 19.1046 20 18C20 16.8954 19.1046 16 18 16C16.8954 16 16 16.8954 16 18C16 19.1046 16.8954 20 18 20Z" fill="currentColor" stroke="black" strokeWidth="0.5px"></path>
                                </g></svg>
                            {t('dropdown_ctrl_grid.dots')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // view modes menu
    const getViewModesMenu = (isBtnViewModesEnabled) => {
        if (!isBtnViewModesEnabled) {
            return;
        }
        if (!viewModesBtnRef.current) {
            return;
        }

        const rect = viewModesBtnRef.current.getBoundingClientRect();
        let top = rect.bottom + window.scrollY;
        let left = rect.left + window.scrollX - 44.5 + 1;

        if (isDesktop1350) {
            left = left + 4;
            top = top - 1;
        }

        return (
            <div
                className={`view-modes-group`}
                ref={viewModesMenuRef}
                style={{ top: `${top}px`, left: `${left}px` }}
            >
                <div className="controls-group">
                    <div className="control">
                        <button
                            className={`control-button control-button-option t_btn_view_mode ${viewMode === 'full' ? 'selected' : ''
                                }`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            onClick={() => {
                                dispatch(setElementsViewMode('full'));
                            }}
                        >
                            <svg className="control-icon-table" fill="#000000" width="17px" height="17px" viewBox="0 0 32 32"><g><path d="M 27 3 H 5 A 2 2 0 0 0 3 5 V 27 a 2 2 0 0 0 2 2 H 27 a 2 2 0 0 0 2 -2 V 5 A 2 2 0 0 0 27 3 Z m 0 4 V 13 H 5 V 7 Z M 17 15 H 27 v 5 H 17 Z m -2 5 H 5 V 15 H 15 Z M 5 22 H 15 v 5 H 5 Z m 12 5 V 22 H 27 v 5 Z"></path></g></svg>
                            {t('dropdown_ctrl_view_modes.full')}
                        </button>
                    </div>

                    <div className="control control-m-t-1">
                        <button
                            className={`control-button control-button-option t_btn_view_mode ${viewMode === 'keys' ? 'selected' : ''
                                }`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            onClick={() => {
                                dispatch(setElementsViewMode('keys'));
                            }}
                        >
                            <svg className="control-icon-key" viewBox="-1 -0.5 24 24" width="18px" height="18px"><path d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path></svg>
                            {t('dropdown_ctrl_view_modes.keys_only')}
                        </button>
                    </div>

                    {/* <div className="control">
                        <button
                            disabled
                            className={`control-button control-button-option disabled ${viewMode === 'names' ? 'selected' : ''}`}
                            // className={`control-button ${viewMode === 'names' ? 'selected' : ''}`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            onClick={() => {
                                dispatch(setElementsViewMode('names'));
                            }}
                        >
                            {t('dropdown_ctrl_view_modes.names_only')}
                        </button>
                    </div> */}
                </div>
            </div>
        );
    };

    // export image menu
    // const getExportImageMenu = (isBtnExportImageEnabled) => {
    function ExportImageMenu({ isBtnExportImageEnabled, exportBtnRef }) {
        if (!isBtnExportImageEnabled) {
            return;
        }

        if (!exportBtnRef.current) {
            return;
        }

        const rect = exportBtnRef.current.getBoundingClientRect();
        const top = rect.bottom + window.scrollY + 2 + 2 - 1;

        // const left = rect.left + window.scrollX + 19 - 80 - 6;
        const left = 10;

        return (
            <div
                className={`export-image-group`}
                ref={exportImageMenuRef}
                style={{ top: `${top}px`, left: `${left}px` }}
            >
                <div className='export-image-title'>{t('m_export_img.title')}</div>

                {/* <div className='export-image-check'>
                    <input
                        type="checkbox"
                        checked={isTransparent}
                        onChange={(e) => { setIsTransparent(e.target.checked); }}
                    />
                    <span>Transparent background</span>
                </div> */}

                <div className="controls-group-new">
                    <div className="control-new">
                        <button
                            className={`control-button-new`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            onClick={() => {
                                dispatch(clickEscape());
                            }}
                        >{t('b.cancel')}</button>
                    </div>

                    <div className="control-new">
                        <button
                            className={`control-button-new primary`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            onClick={() => {
                                dispatch(enableExportImageClick());
                            }}
                        >{t('b.export')}</button>
                    </div>
                </div>
            </div>
        );
    };

    // export code menu
    const getExportCodeMenu = (isBtnExportCodeEnabled) => {
        if (!isBtnExportCodeEnabled) {
            return;
        }
        if (!exportBtnRef.current) {
            return;
        }

        const rect = exportBtnRef.current.getBoundingClientRect();
        let top = rect.bottom + window.scrollY + 47 + 1;
        // const left = rect.left + window.scrollX + 99;

        let left = rect.left + window.scrollX + 99 + 5;
        // left =
        //     ['de', 'tr'].includes(i18n.language)
        //         ? left + 15
        //         : ['es', 'fr', 'pt-BR'].includes(i18n.language)
        //             ? left + 16
        //             : i18n.language === 'pl'
        //                 ? left + 4
        //                 : i18n.language === 'ja'
        //                     ? left + 24
        //                     : i18n.language === 'it'
        //                         ? left + 13
        //                         : left;

        top = top - 5 - 4 + 2 - 2;
        left = left + 2 - 13 + 1 - 1 + 22;

        if (isDesktop1350) {
            left = left - 33.5 + 8;
            top = top - 15 - 1;
        }

        return (
            <div
                className={`export-code-group`}
                ref={exportCodeMenuRef}
                style={{ top: `${top}px`, left: `${left}px` }}
            >
                <div className="controls-group">
                    <div className="control">
                        <button
                            className={`control-button control-button-option`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => { dispatch(toggleModalDiagramExportSQL('mysql')) }}
                        >
                            MySQL
                        </button>
                    </div>

                    <div className="control">
                        <button
                            className={`control-button control-button-option`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => { dispatch(toggleModalDiagramExportSQL('postgresql')) }}
                        >
                            PostgreSQL
                        </button>
                    </div>

                    <div className="control">
                        <button
                            className={`control-button control-button-option`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => { dispatch(toggleModalDiagramExportSQL('sqlserver')) }}
                        >
                            SQL Server
                        </button>
                    </div>

                    <div className="control">
                        <button
                            className={`control-button control-button-option`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => { dispatch(toggleModalDiagramExportSQL('oracle')) }}
                        >
                            Oracle
                        </button>
                    </div>

                    <div className="control">
                        <button
                            className={`control-button control-button-option`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => { dispatch(toggleModalDiagramExportSQL('mariadb')) }}
                        >
                            MariaDB
                        </button>
                    </div>

                    <div className="control">
                        <button
                            disabled
                            className={`control-button control-button-option disabled`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                        // onClick={() => { dispatch(toggleModalDiagramExportSQL('sqlite')) }}
                        >
                            SQLite
                        </button>
                    </div>


                    {/* NOT USED NOW */}
                    {/* <div className="control">
                        <button
                            disabled
                            className={`control-button control-button-option disabled`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => {
                                dispatch(clickEscape());
                            }}
                        >
                            IBM Db2
                        </button>
                    </div>

                    <div className="control">
                        <button
                            disabled
                            className={`control-button control-button-option disabled`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => {
                                dispatch(clickEscape());
                            }}
                        >
                            Databricks
                        </button>
                    </div>

                    <div className="control">
                        <button
                            disabled
                            className={`control-button control-button-option disabled`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => {
                                dispatch(clickEscape());
                            }}
                        >
                            Snowflake
                        </button>
                    </div>

                    <div className="control">
                        <button
                            disabled
                            className={`control-button control-button-option disabled`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => {
                                dispatch(clickEscape());
                            }}
                        >
                            BigQuery
                        </button>
                    </div> */}


                    {/* <div className="control">
                        <button
                            className={`control-button control-button-option`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => {
                                dispatch(clickEscape());
                            }}
                        >
                            Redshift
                        </button>
                    </div>  */}
                </div>

                {/* <div className="control-divider-horizontal"></div> */}

                {/* <div className="controls-group">
                    <div className="control">
                        <button
                            disabled
                            className={`control-button control-button-option disabled`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => {
                                dispatch(clickEscape());
                            }}
                        >
                            ORMs
                        </button>
                    </div>
                </div> */}
            </div>
        );
    };

    // export menu
    const getExportMenu = (isBtnExportEnabled) => {
        if (!isBtnExportEnabled) {
            return;
        }

        if (!exportBtnRef.current) {
            return;
        }

        const rect = exportBtnRef.current.getBoundingClientRect();
        let top = rect.bottom + window.scrollY;
        // const left = rect.left + window.scrollX - 55;

        let left = rect.left + window.scrollX + 19 - 80;
        left = left - 316;
        // left =
        //     ['de', 'tr'].includes(i18n.language)
        //         ? left - 26
        //         : ['es', 'fr', 'pt-BR'].includes(i18n.language)
        //             ? left - 15
        //             : i18n.language === 'pl'
        //                 ? left - 7
        //                 : i18n.language === 'ja'
        //                     ? left - 32
        //                     : i18n.language === 'it'
        //                         ? left - 10
        //                         : left;

        if (isDesktop1350) {
            left = left + 8;
            top = top - 1;
        }


        return (
            <div
                className={`export-group`}
                ref={exportMenuRef}
                style={{ top: `${top}px`, right: `${left}px` }}
                onMouseLeave={() => {
                    // Only cancel a still-pending open — do NOT close the export-code
                    // sub-menu on leave. It stays open until another menu item is
                    // hovered (Export Image) or the user clicks outside.
                    cancelOpenExportCodeTimer();
                }}
            >
                <div className="controls-group">
                    <div className="control">
                        <button
                            className={`control-button control-button-option`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            onMouseEnter={() => {
                                cancelOpenExportCodeTimer();
                                if (isBtnExportCodeEnabled) dispatch(toggleBtnExportCode());
                            }}
                            onClick={() => {
                                dispatch(toggleBtnExportImage());
                            }}
                        >
                            <svg className="icon-export-img" width="13px" height="15px" viewBox="0 0 32 32" fill="#000000"><g><g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd"> <g transform="translate(-360.000000, -99.000000)" fill="rgb(235, 244, 247)"> <path d="M368,109 C366.896,109 366,108.104 366,107 C366,105.896 366.896,105 368,105 C369.104,105 370,105.896 370,107 C370,108.104 369.104,109 368,109 L368,109 Z M368,103 C365.791,103 364,104.791 364,107 C364,109.209 365.791,111 368,111 C370.209,111 372,109.209 372,107 C372,104.791 370.209,103 368,103 L368,103 Z M390,116.128 L384,110 L374.059,120.111 L370,116 L362,123.337 L362,103 C362,101.896 362.896,101 364,101 L388,101 C389.104,101 390,101.896 390,103 L390,116.128 L390,116.128 Z M390,127 C390,128.104 389.104,129 388,129 L382.832,129 L375.464,121.535 L384,112.999 L390,118.999 L390,127 L390,127 Z M364,129 C362.896,129 362,128.104 362,127 L362,126.061 L369.945,118.945 L380.001,129 L364,129 L364,129 Z M388,99 L364,99 C361.791,99 360,100.791 360,103 L360,127 C360,129.209 361.791,131 364,131 L388,131 C390.209,131 392,129.209 392,127 L392,103 C392,100.791 390.209,99 388,99 L388,99 Z" fill="white" stroke="white"> </path> </g> </g> </g></svg>
                            {t('dropdown_ctrl_export.export_img')}
                        </button>
                    </div>

                    <div className="control control-m-t-1">
                        <button
                            className={`control-button control-button-option code ${isBtnExportCodeEnabled ? 'selected' : ''}`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            onMouseEnter={() => {
                                if (isBtnExportCodeEnabled) return;
                                cancelOpenExportCodeTimer();
                                openExportCodeTimerRef.current = setTimeout(() => {
                                    openExportCodeTimerRef.current = null;
                                    dispatch(toggleBtnExportCode());
                                }, 150);
                            }}
                            onMouseLeave={cancelOpenExportCodeTimer}
                        >
                            <svg className="icon-export-code" width="20px" height="21px" viewBox="0 0 24 24" fill="none"><g> <path d="M7 8L3 11.6923L7 16M17 8L21 11.6923L17 16M14 4L10 20" stroke="#ffffff" strokeWidth="1.8px" strokeLinecap="round" strokeLinejoin="round"></path> </g></svg>
                            {t('dropdown_ctrl_export.export_code')}
                            <svg width="16px" height="16px" fill="none" viewBox="0 -2 24 24" stroke="#8e94afb3" strokeWidth="2px" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px', marginRight: '-3px', marginTop: '-1px' }}><path d="m9 18 6-6-6-6"></path></svg>
                        </button>
                    </div>

                    {/* <div className="control">
                        <button
                            disabled
                            className={`control-button control-button-option disabled`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => {
                                dispatch(toggleBtnExportCode());
                            }}
                        >
                            Export Document
                        </button>
                    </div> */}

                    {/* <div className="control">
                        <button

                            className={`control-button `}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => {
                                dispatch(toggleBtnExportCode());
                            }}
                        >
                            Generate Sample Data
                        </button>
                    </div> */}

                </div>
            </div>
        );
    };



    // settings menu
    const getSettingsMenu = (isBtnSettingsEnabled) => {
        if (!isBtnSettingsEnabled) {
            return;
        }

        if (!settingsBtnRef.current) {
            return;
        }

        const rect = settingsBtnRef.current.getBoundingClientRect();
        let top = rect.bottom + window.scrollY + 1 - 0.5;
        // const left = rect.left + window.scrollX - 55;
        let left = rect.left + window.scrollX - 5;


        if (isDesktop1350) {
            top = top - 2;
        }

        return (
            <div
                className={`settings-group`}
                ref={settingsMenuRef}
                style={{ top: `${top}px`, left: `${left}px` }}
                onMouseOver={handleSettingsMenuMouseOver}
                onMouseLeave={() => {
                    // Only cancel a still-pending open — an already-open sub-menu stays
                    // open until another menu item is hovered or the menu closes.
                    cancelOpenSubMenuTimer();
                }}
            >
                <div className="controls-group">
                    {isDesktop ?
                        <React.Fragment>
                            <div className="control">
                                <button
                                    className={`control-button control-button-with-key`}
                                    type="button"
                                    onClick={() => {
                                        dispatch(toggleBtnSettings());
                                        setTimeout(() => { window.open('/settings/preferences/', "_blank", "noopener,noreferrer"); }, 100)
                                    }}
                                >
                                    <div className="control-button-with-key-left-part">
                                        <svg className="control-icon-preferences" width="14px" height="20px" viewBox="0 0 24 24" fill="none"><g> <path d="M2 20C2 19.4477 2.44772 19 3 19H10C10.5523 19 11 19.4477 11 20C11 20.5523 10.5523 21 10 21H3C2.44772 21 2 20.5523 2 20Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M15 19V17C15 16.4477 14.5523 16 14 16C13.4477 16 13 16.4477 13 17V23C13 23.5523 13.4477 24 14 24C14.5523 24 15 23.5523 15 23V21H21C21.5523 21 22 20.5523 22 20C22 19.4477 21.5523 19 21 19H15Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13L21 13C21.5523 13 22 12.5523 22 12C22 11.4477 21.5523 11 21 11L12 11Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M2 12C2 11.4477 2.44772 11 3 11H7V9C7 8.44772 7.44772 8 8 8C8.55228 8 9 8.44772 9 9V15C9 15.5523 8.55228 16 8 16C7.44772 16 7 15.5523 7 15V13H3C2.44772 13 2 12.5523 2 12Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M15 3V1C15 0.447716 14.5523 0 14 0C13.4477 0 13 0.447716 13 1V7C13 7.55228 13.4477 8 14 8C14.5523 8 15 7.55228 15 7V5H21C21.5523 5 22 4.55228 22 4C22 3.44772 21.5523 3 21 3H15Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M2 4C2 4.55228 2.44772 5 3 5H10C10.5523 5 11 4.55228 11 4C11 3.44772 10.5523 3 10 3H3C2.44772 3 2 3.44772 2 4Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> </g></svg>
                                        <span>{t('dropdown_ctrl_settings.preferences')}</span>
                                    </div>
                                    {/* <div className="control-button-with-key-right-part">
                                        <span className="control-button-with-key-right-part-text">{shortKeysMap?.CTRL}</span>
                                        <span className="control-button-with-key-right-part-connector">+</span>
                                        <span className="control-button-with-key-right-part-text">.</span>
                                    </div> */}
                                </button>
                            </div>

                            <div className="control-divider"></div>

                            <div className="control">
                                <button
                                    className={`control-button control-button-with-key`}
                                    style={{ justifyContent: 'unset' }}
                                    type="button"
                                    onClick={() => {
                                        dispatch(toggleBtnSettings());
                                        setTimeout(() => { dispatch(toggleDiagramCreateNew('canvas', diagramId)); }, 100)
                                    }}
                                >
                                    {/* <svg className="control-icon-new-diagram" fill="white" width="16px" height="16px" viewBox="0 0 32 32"><g><path d="M27.2,8.22H23.78V5.42A3.42,3.42,0,0,0,20.36,2H5.42A3.42,3.42,0,0,0,2,5.42V20.36a3.42,3.42,0,0,0,3.42,3.42h2.8V27.2A2.81,2.81,0,0,0,11,30H27.2A2.81,2.81,0,0,0,30,27.2V11A2.81,2.81,0,0,0,27.2,8.22ZM5.42,21.91a1.55,1.55,0,0,1-1.55-1.55V5.42A1.54,1.54,0,0,1,5.42,3.87H20.36a1.55,1.55,0,0,1,1.55,1.55v2.8H11A2.81,2.81,0,0,0,8.22,11V21.91ZM28.13,27.2a.93.93,0,0,1-.93.93H11a.93.93,0,0,1-.93-.93V11a.93.93,0,0,1,.93-.93H27.2a.93.93,0,0,1,.93.93Z"></path><path d="M24.09,18.18H20v-4a.93.93,0,1,0-1.86,0v4h-4a.93.93,0,0,0,0,1.86h4v4.05a.93.93,0,1,0,1.86,0V20h4.05a.93.93,0,1,0,0-1.86Z"></path></g></svg> */}
                                    {/* <svg className="control-icon-new-diagram" fill="#000000" width="22px" height="23px" viewBox="0 0 24 24"><path d="M12,18c-0.6,0-1-0.4-1-1V7c0-0.6,0.4-1,1-1s1,0.4,1,1v10C13,17.6,12.6,18,12,18z" fill="currentColor"></path><path d="M17,13H7c-0.6,0-1-0.4-1-1s0.4-1,1-1h10c0.6,0,1,0.4,1,1S17.6,13,17,13z" fill="currentColor"></path></svg>
                                    {t('dropdown_ctrl_settings.new_project')} */}
                                    <div className="control-button-with-key-left-part">
                                        <svg className="control-icon-new-diagram" fill="#000000" width="22px" height="23px" viewBox="0 0 24 24"><path d="M12,18c-0.6,0-1-0.4-1-1V7c0-0.6,0.4-1,1-1s1,0.4,1,1v10C13,17.6,12.6,18,12,18z" fill="currentColor" stroke="black" strokeWidth="0.1px"></path><path d="M17,13H7c-0.6,0-1-0.4-1-1s0.4-1,1-1h10c0.6,0,1,0.4,1,1S17.6,13,17,13z" fill="currentColor" stroke="black" strokeWidth="0.1px"></path></svg>
                                        <span>{t('dropdown_ctrl_settings.new_project')}</span>
                                    </div>
                                    <div className="control-button-with-key-right-part">
                                        <span className="control-button-with-key-right-part-text">{shortKeysMap?.CTRL}</span>
                                        <span className="control-button-with-key-right-part-connector">+</span>
                                        <span className="control-button-with-key-right-part-text text_shift">{shortKeysMap?.SHIFT}</span>
                                        <span className="control-button-with-key-right-part-connector">+</span>
                                        <span className="control-button-with-key-right-part-text">O</span>
                                    </div>
                                </button>
                            </div>

                            {/* <div className="control control-m-1-3">
                                <button
                                    className={`control-button`}
                                    type="button"
                                    onClick={() => {
                                        dispatch(toggleBtnSettings());
                                        setTimeout(() => { dispatch(toggleDiagramCreateNew('canvas', diagramId)); }, 100)
                                    }}
                                >
                                    <svg className="control-icon-import" width="17px" height="18px" viewBox="0 0 24 24" fill="none"><g> <path d="M16 11L12 15M12 15L8 11M12 15V3M21 15V17C21 18.1046 20.1046 19 19 19H5C3.89543 19 3 18.1046 3 17V15" stroke="#000000" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"></path> </g></svg>
                                    {t('dropdown_ctrl_settings.import_diagram')}
                                </button>
                            </div> */}

                            <div className="control">
                                <button
                                    className={`control-button`}
                                    type="button"
                                    onClick={() => {
                                        dispatch(toggleBtnSettings());
                                        setTimeout(() => { dispatch(toggleModalDiagramClone('canvas', diagramId)); }, 100)
                                    }}
                                >
                                    <svg className="control-icon-duplicate" fill="white" width="16px" height="16px" viewBox="0 0 32 32"><g><path stroke="white" strokeWidth="0.8px" fill="#e6e6e6" d="M27.2,8.22H23.78V5.42A3.42,3.42,0,0,0,20.36,2H5.42A3.42,3.42,0,0,0,2,5.42V20.36a3.42,3.42,0,0,0,3.42,3.42h2.8V27.2A2.81,2.81,0,0,0,11,30H27.2A2.81,2.81,0,0,0,30,27.2V11A2.81,2.81,0,0,0,27.2,8.22ZM5.42,21.91a1.55,1.55,0,0,1-1.55-1.55V5.42A1.54,1.54,0,0,1,5.42,3.87H20.36a1.55,1.55,0,0,1,1.55,1.55v2.8H11A2.81,2.81,0,0,0,8.22,11V21.91ZM28.13,27.2a.93.93,0,0,1-.93.93H11a.93.93,0,0,1-.93-.93V11a.93.93,0,0,1,.93-.93H27.2a.93.93,0,0,1,.93.93Z"></path></g></svg>
                                    {t('dropdown_ctrl_settings.duplicate_diagram')}
                                </button>
                            </div>

                            <div className="control-divider"></div>

                            <div className="control">
                                <button
                                    ref={viewTriggerBtnRef}
                                    className={`control-button control-button-with-key view-trigger ${openSettingsSubMenu === 'view' ? 'selected' : ''}`}
                                    type="button"
                                    onMouseEnter={() => {
                                        if (openSettingsSubMenu === 'view') return;
                                        if (openSettingsSubMenu === 'language') setOpenSettingsSubMenu(null);
                                        cancelOpenSubMenuTimer();
                                        openSubMenuTimerRef.current = setTimeout(() => {
                                            openSubMenuTimerRef.current = null;
                                            setOpenSettingsSubMenu('view');
                                        }, 150);
                                    }}
                                    onMouseLeave={cancelOpenSubMenuTimer}
                                    onClick={() => {
                                        cancelOpenSubMenuTimer();
                                        setOpenSettingsSubMenu('view');
                                    }}
                                >
                                    <div className="control-button-with-key-left-part">
                                        <svg width="20px" height="20px" viewBox="0 -1 24 24" fill="none" style={{ marginLeft: '-4px', marginRight: '-4px' }}><path fillRule="evenodd" clipRule="evenodd" d="M12.0001 5.25C9.22586 5.25 6.79699 6.91121 5.12801 8.44832C4.28012 9.22922 3.59626 10.0078 3.12442 10.5906C2.88804 10.8825 2.70368 11.1268 2.57736 11.2997C2.51417 11.3862 2.46542 11.4549 2.43187 11.5029C2.41509 11.5269 2.4021 11.5457 2.393 11.559L2.38227 11.5747L2.37911 11.5794L2.10547 12.0132L2.37809 12.4191L2.37911 12.4206L2.38227 12.4253L2.393 12.441C2.4021 12.4543 2.41509 12.4731 2.43187 12.4971C2.46542 12.5451 2.51417 12.6138 2.57736 12.7003C2.70368 12.8732 2.88804 13.1175 3.12442 13.4094C3.59626 13.9922 4.28012 14.7708 5.12801 15.5517C6.79699 17.0888 9.22586 18.75 12.0001 18.75C14.7743 18.75 17.2031 17.0888 18.8721 15.5517C19.72 14.7708 20.4039 13.9922 20.8757 13.4094C21.1121 13.1175 21.2964 12.8732 21.4228 12.7003C21.4859 12.6138 21.5347 12.5451 21.5682 12.4971C21.585 12.4731 21.598 12.4543 21.6071 12.441L21.6178 12.4253L21.621 12.4206L21.6224 12.4186L21.9035 12L21.622 11.5809L21.621 11.5794L21.6178 11.5747L21.6071 11.559C21.598 11.5457 21.585 11.5269 21.5682 11.5029C21.5347 11.4549 21.4859 11.3862 21.4228 11.2997C21.2964 11.1268 21.1121 10.8825 20.8757 10.5906C20.4039 10.0078 19.72 9.22922 18.8721 8.44832C17.2031 6.91121 14.7743 5.25 12.0001 5.25ZM4.29022 12.4656C4.14684 12.2885 4.02478 12.1311 3.92575 12C4.02478 11.8689 4.14684 11.7115 4.29022 11.5344C4.72924 10.9922 5.36339 10.2708 6.14419 9.55168C7.73256 8.08879 9.80369 6.75 12.0001 6.75C14.1964 6.75 16.2676 8.08879 17.8559 9.55168C18.6367 10.2708 19.2709 10.9922 19.7099 11.5344C19.8533 11.7115 19.9753 11.8689 20.0744 12C19.9753 12.1311 19.8533 12.2885 19.7099 12.4656C19.2709 13.0078 18.6367 13.7292 17.8559 14.4483C16.2676 15.9112 14.1964 17.25 12.0001 17.25C9.80369 17.25 7.73256 15.9112 6.14419 14.4483C5.36339 13.7292 4.72924 13.0078 4.29022 12.4656ZM14.25 12C14.25 13.2426 13.2427 14.25 12 14.25C10.7574 14.25 9.75005 13.2426 9.75005 12C9.75005 10.7574 10.7574 9.75 12 9.75C13.2427 9.75 14.25 10.7574 14.25 12ZM15.75 12C15.75 14.0711 14.0711 15.75 12 15.75C9.92898 15.75 8.25005 14.0711 8.25005 12C8.25005 9.92893 9.92898 8.25 12 8.25C14.0711 8.25 15.75 9.92893 15.75 12Z" fill="white" strokeWidth="0.1px"></path></svg>
                                        <span>{t('dropdown_ctrl_settings.view')}</span>
                                    </div>
                                    <div className="control-button-with-key-right-part">
                                        <svg className="setting-icon-arrow" width="16px" height="18px" fill="none" viewBox="0 -2 24 24" stroke="#8e94afb3" strokeWidth="2px" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" stroke="rgb(134, 145, 148)"></path></svg>
                                    </div>
                                </button>

                                {renderViewFlyout('input_mode', [
                                    { value: 'auto', labelKey: 'opt_auto' },
                                    { value: 'trackpad', labelKey: 'opt_trackpad' },
                                    { value: 'mouse', labelKey: 'opt_mouse' },
                                ], diagramPrefs.input_mode, (v) => {
                                    dispatch(preferencesPatchRequest({ diagram: { input_mode: v } }));
                                })}

                                {renderViewFlyout('background', [
                                    { value: 'default', labelKey: 'opt_default' },
                                    { value: 'gray', labelKey: 'opt_gray' },
                                    { value: 'blue', labelKey: 'opt_blue' },
                                    { value: 'purple', labelKey: 'opt_purple' },
                                    { value: 'violet', labelKey: 'opt_violet' },
                                    { value: 'green', labelKey: 'opt_green' },
                                    { value: 'red', labelKey: 'opt_red' },
                                    { value: 'yellow', labelKey: 'opt_yellow' },
                                ], diagramPrefs.background, (v) => {
                                    dispatch(preferencesPatchRequest({ diagram: { background: v } }));
                                })}

                                {renderViewFlyout('grid', [
                                    { value: 'dots', labelKey: 'opt_dots' },
                                    { value: 'lines', labelKey: 'opt_lines' },
                                    { value: 'none', labelKey: 'opt_none' },
                                ], gridStyle || 'none', (v) => {
                                    patchGridPref(v === 'none' ? null : v);
                                })}

                                {renderViewFlyout('view_mode', [
                                    { value: 'full', labelKey: 'opt_full' },
                                    { value: 'keys', labelKey: 'opt_keys' },
                                    // { value: 'names', labelKey: 'opt_names' },
                                ], viewMode, (v) => {
                                    // view_mode is session-only UI state — not a preference and
                                    // (user decision) not persisted in the viewport either:
                                    // every diagram load starts at 'full'.
                                    dispatch(setElementsViewMode(v));
                                })}

                                {openSettingsSubMenu === 'view' && (
                                    <div className="settings-sub-menu">
                                        <div className="settings-sub-menu-list settings-views-sub-menu-list">
                                            <div className="control">
                                                <button
                                                    className={`control-button control-button-with-key view-trigger ${openViewFlyout === 'input_mode' ? 'selected' : ''}`}
                                                    type="button"
                                                    {...flyoutTriggerProps('input_mode')}
                                                >
                                                    <div className="control-button-with-key-left-part">
                                                        <svg width="20px" height="64px" viewBox="0 -1 24 24" fill="none" style={{ width: '20px', height: '20px', marginLeft: '-5px', marginRight: '-6px' }}><g> <path d="M5 9C5 5.13401 8.13401 2 12 2C15.866 2 19 5.13401 19 9V15C19 18.866 15.866 22 12 22C8.13401 22 5 18.866 5 15V9Z" strokeWidth="1.5" stroke="#f3f3f3"></path> <path d="M10.5 8.5C10.5 7.67157 11.1716 7 12 7C12.8284 7 13.5 7.67157 13.5 8.5V10.5C13.5 11.3284 12.8284 12 12 12C11.1716 12 10.5 11.3284 10.5 10.5V8.5Z" strokeWidth="1.5" stroke="#f3f3f3"></path> <path d="M12 2V7" stroke="#f3f3f3" strokeWidth="1.5" strokeLinecap="round" stroke="#f3f3f3"></path> </g></svg>
                                                        <span className="settings-sub-menu-name">{t('dropdown_ctrl_settings.mouse_trackpad')}</span>
                                                    </div>
                                                    <div className="control-button-with-key-right-part">
                                                        <svg className="setting-icon-arrow" width="16px" height="18px" fill="none" viewBox="0 -2 24 24" stroke="#8e94afb3" strokeWidth="2px" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" stroke="rgb(134, 145, 148)"></path></svg>
                                                    </div>
                                                </button>
                                            </div>

                                            <div className="control-divider"></div>

                                            <div className="control">
                                                <button
                                                    className={`control-button control-button-with-key view-trigger ${openViewFlyout === 'background' ? 'selected' : ''}`}
                                                    type="button"
                                                    {...flyoutTriggerProps('background')}
                                                >
                                                    <div className="control-button-with-key-left-part">
                                                        <svg className="icon-settings-background" width="20px" height="18px" viewBox="-0.5 0 24 24" fill="#000000" style={{ marginLeft: '-5px', marginRight: '-5px', marginTop: '1px' }}><g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd"> <g fill="#212121" fillRule="nonzero"> <path d="M12,22 C17.5228475,22 22,17.5228475 22,12 C22,6.4771525 17.5228475,2 12,2 C6.4771525,2 2,6.4771525 2,12 C2,17.5228475 6.4771525,22 12,22 Z M12,20 L12,4 C16.418278,4 20,7.581722 20,12 C20,16.418278 16.418278,20 12,20 Z" fill="#dbdbdb"> </path> </g> </g> </svg>
                                                        <span className="settings-sub-menu-name">{t('dropdown_ctrl_settings.background_color')}</span>
                                                    </div>
                                                    <div className="control-button-with-key-right-part">
                                                        <svg className="setting-icon-arrow" width="16px" height="18px" fill="none" viewBox="0 -2 24 24" stroke="#8e94afb3" strokeWidth="2px" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" stroke="rgb(134, 145, 148)"></path></svg>
                                                    </div>
                                                </button>
                                            </div>
                                            <div className="control">
                                                <button
                                                    className={`control-button control-button-with-key view-trigger ${openViewFlyout === 'grid' ? 'selected' : ''}`}
                                                    type="button"
                                                    {...flyoutTriggerProps('grid')}
                                                >
                                                    <div className="control-button-with-key-left-part">
                                                        <svg width="16px" height="19px" viewBox="1 0 48 48" fill="#000000" style={{ marginTop: '1px', marginLeft: '-2px', marginRight: '-3px', width: '16px' }}> <path d="M44,26a2,2,0,0,0,0-4H38V14h6a2,2,0,0,0,0-4H38V4a2,2,0,0,0-4,0v6H26V4a2,2,0,0,0-4,0v6H14V4a2,2,0,0,0-4,0v6H4a2,2,0,0,0,0,4h6v8H4a2,2,0,0,0,0,4h6v8H4a2,2,0,0,0,0,4h6v6a2,2,0,0,0,4,0V38h8v6a2,2,0,0,0,4,0V38h8v6a2,2,0,0,0,4,0V38h6a2,2,0,0,0,0-4H38V26ZM34,14v8H26V14ZM14,14h8v8H14Zm0,20V26h8v8Zm20,0H26V26h8Z" fill="#f3f3f3" stroke="black" strokeWidth="1.2px"></path></svg>
                                                        <span className="settings-sub-menu-name">{t('dropdown_ctrl_settings.grid_layout')}</span>
                                                    </div>
                                                    <div className="control-button-with-key-right-part">
                                                        <svg className="setting-icon-arrow" width="16px" height="18px" fill="none" viewBox="0 -2 24 24" stroke="#8e94afb3" strokeWidth="2px" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" stroke="rgb(134, 145, 148)"></path></svg>
                                                    </div>
                                                </button>
                                            </div>
                                            <div className="control">
                                                <button
                                                    className={`control-button control-button-with-key view-trigger ${openViewFlyout === 'view_mode' ? 'selected' : ''}`}
                                                    type="button"
                                                    {...flyoutTriggerProps('view_mode')}
                                                >
                                                    <div className="control-button-with-key-left-part">
                                                        <svg className="icon-view-mode" height="22px" width="20px" viewBox="0 -5 512 512" style={{ marginLeft: '-2px', marginRight: '-3px', width: '16px' }}><path d="M496,324.362h-31.702v-60.01c0-8.836-7.164-16-16-16H271.996v-60.714h127.109c8.836,0,16-7.164,16-16V76.235 c0-8.836-7.164-16-16-16h-286.21c-8.836,0-16,7.164-16,16v95.404c0,8.836,7.164,16,16,16h127.101v60.714H63.702 c-8.836,0-16,7.164-16,16v60.01H16c-8.836,0-16,7.164-16,16v95.404c0,8.836,7.164,16,16,16h95.404c8.836,0,16-7.164,16-16v-95.404 c0-8.836-7.164-16-16-16H79.702v-44.01h160.293v44.01h-31.698c-8.836,0-16,7.164-16,16v95.404c0,8.836,7.164,16,16,16h95.404 c8.836,0,16-7.164,16-16v-95.404c0-8.836-7.164-16-16-16h-31.707v-44.01h160.302v44.01h-31.702c-8.836,0-16,7.164-16,16v95.404 c0,8.836,7.164,16,16,16H496c8.836,0,16-7.164,16-16v-95.404C512,331.525,504.836,324.362,496,324.362z M95.404,356.362v63.404H32 v-63.404H95.404z M287.702,356.362v63.404h-63.404v-63.404H287.702z M128.895,155.638V92.235h254.21v63.404H128.895z M480,419.765 h-63.404v-63.404H480V419.765z"></path></svg>
                                                        <span className="settings-sub-menu-name">{t('dropdown_ctrl_settings.view_mode')}</span>
                                                    </div>
                                                    <div className="control-button-with-key-right-part">
                                                        <svg className="setting-icon-arrow" width="16px" height="18px" fill="none" viewBox="0 -2 24 24" stroke="#8e94afb3" strokeWidth="2px" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" stroke="rgb(134, 145, 148)"></path></svg>
                                                    </div>
                                                </button>
                                            </div>

                                            <div className="control-divider"></div>

                                            <div className="control">
                                                <button
                                                    className={`control-button control-button-with-key view-trigger ${openSettingsSubMenu !== 'view' ? 'selected' : ''}`}
                                                    type="button"
                                                    onMouseEnter={closeViewFlyout}
                                                    onClick={() => toggleDiagramPref('show_align_lines')}
                                                >
                                                    <div className="control-button-with-key-left-part">
                                                        <svg width="64px" height="64px" viewBox="0 0 24 24" style={{ width: '17px', height: '20px', marginLeft: '-1px', marginRight: '-5px' }}><g> <path fillRule="evenodd" clipRule="evenodd" d="M3.375 1.625C3.78921 1.625 4.125 1.96079 4.125 2.375L4.125 22.375C4.125 22.7892 3.78921 23.125 3.375 23.125C2.96079 23.125 2.625 22.7892 2.625 22.375L2.625 2.375C2.625 1.96079 2.96079 1.625 3.375 1.625Z" fill="white" strokeWidth="0"></path> <path d="M7.375 7.875C7.375 6.94038 7.375 6.47308 7.57596 6.125C7.70761 5.89697 7.89697 5.70761 8.125 5.57596C8.47308 5.375 8.94038 5.375 9.875 5.375H18.875C19.8096 5.375 20.2769 5.375 20.625 5.57596C20.853 5.70761 21.0424 5.89697 21.174 6.125C21.375 6.47308 21.375 6.94038 21.375 7.875C21.375 8.80962 21.375 9.27692 21.174 9.625C21.0424 9.85303 20.853 10.0424 20.625 10.174C20.2769 10.375 19.8096 10.375 18.875 10.375L9.875 10.375C8.94038 10.375 8.47308 10.375 8.125 10.174C7.89697 10.0424 7.70761 9.85303 7.57596 9.625C7.375 9.27692 7.375 8.80962 7.375 7.875Z" fill="white" strokeWidth="0"></path> <path d="M7.375 16.875C7.375 15.9404 7.375 15.4731 7.57596 15.125C7.70761 14.897 7.89697 14.7076 8.125 14.576C8.47308 14.375 8.94038 14.375 9.875 14.375L15.875 14.375C16.8096 14.375 17.2769 14.375 17.625 14.576C17.853 14.7076 18.0424 14.897 18.174 15.125C18.375 15.4731 18.375 15.9404 18.375 16.875C18.375 17.8096 18.375 18.2769 18.174 18.625C18.0424 18.853 17.853 19.0424 17.625 19.174C17.2769 19.375 16.8096 19.375 15.875 19.375H9.875C8.94038 19.375 8.47308 19.375 8.125 19.174C7.89697 19.0424 7.70761 18.853 7.57596 18.625C7.375 18.2769 7.375 17.8096 7.375 16.875Z" fill="white" strokeWidth="0"></path> </g></svg>
                                                        <span className="settings-sub-menu-name">{t('dropdown_ctrl_settings.show_align_lines')}</span>
                                                    </div>
                                                    <div className="control-button-with-key-right-part">
                                                        <CtlSwitch on={!!diagramPrefs.show_align_lines} />
                                                    </div>
                                                </button>
                                            </div>
                                            <div className="control">
                                                <button
                                                    className={`control-button control-button-with-key view-trigger ${openSettingsSubMenu !== 'view' ? 'selected' : ''}`}
                                                    type="button"
                                                    onMouseEnter={closeViewFlyout}
                                                    onClick={() => toggleDiagramPref('show_cursors')}
                                                >
                                                    <div className="control-button-with-key-left-part">
                                                        <svg width="18px" height="26px" viewBox="0.5 0 24 24" style={{ width: '18px', marginLeft: '-2px', marginRight: '-5px' }}><path d="M17.2607 12.4008C19.3774 11.2626 20.4357 10.6935 20.7035 10.0084C20.9359 9.41393 20.8705 8.74423 20.5276 8.20587C20.1324 7.58551 18.984 7.23176 16.6872 6.52425L8.00612 3.85014C6.06819 3.25318 5.09923 2.95471 4.45846 3.19669C3.90068 3.40733 3.46597 3.85584 3.27285 4.41993C3.051 5.06794 3.3796 6.02711 4.03681 7.94545L6.94793 16.4429C7.75632 18.8025 8.16052 19.9824 8.80519 20.3574C9.36428 20.6826 10.0461 20.7174 10.6354 20.4507C11.3149 20.1432 11.837 19.0106 12.8813 16.7454L13.6528 15.0719C13.819 14.7113 13.9021 14.531 14.0159 14.3736C14.1168 14.2338 14.2354 14.1078 14.3686 13.9984C14.5188 13.8752 14.6936 13.7812 15.0433 13.5932L17.2607 12.4008Z" fill="#e5e5e5" stroke="rgb(32, 35, 40)" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                                                        <span className="settings-sub-menu-name">{t('dropdown_ctrl_settings.show_members_cursors')}</span>
                                                    </div>
                                                    <div className="control-button-with-key-right-part">
                                                        <CtlSwitch on={!!diagramPrefs.show_cursors} />
                                                    </div>
                                                </button>
                                            </div>
                                            <div className="control">
                                                <button
                                                    className={`control-button control-button-with-key view-trigger ${openSettingsSubMenu !== 'view' ? 'selected' : ''}`}
                                                    type="button"
                                                    onMouseEnter={closeViewFlyout}
                                                    onClick={() => toggleDiagramPref('show_comments')}
                                                >
                                                    <div className="control-button-with-key-left-part">
                                                        <svg width="15px" height="25px" viewBox="0 1 24 24" stroke="black" fill="#f0f0f0" strokeWidth="0" style={{ width: '15px', marginLeft: '-1px', marginRight: '-2px', marginTop: '2px' }}><g><path d="M20,20h-4c-0.6,0-1-0.4-1-1s0.4-1,1-1h4c1.1,0,2-0.9,2-2V4c0-1.1-0.9-2-2-2H4C2.9,2,2,2.9,2,4v12c0,1.1,0.9,2,2,2h4 c0.6,0,1,0.4,1,1s-0.4,1-1,1H4c-2.2,0-4-1.8-4-4V4c0-2.2,1.8-4,4-4h16c2.2,0,4,1.8,4,4v12C24,18.2,22.2,20,20,20z"></path></g><g><path d="M12,24c-0.3,0-0.5-0.1-0.7-0.3l-4-4c-0.4-0.4-0.4-1,0-1.4s1-0.4,1.4,0l3.3,3.3l3.3-3.3c0.4-0.4,1-0.4,1.4,0s0.4,1,0,1.4 l-4,4C12.5,23.9,12.3,24,12,24z"></path></g><g> <path d="M18,7H6C5.4,7,5,6.6,5,6s0.4-1,1-1h12c0.6,0,1,0.4,1,1S18.6,7,18,7z"></path> </g><g> <path d="M13,11H6c-0.6,0-1-0.4-1-1s0.4-1,1-1h7c0.6,0,1,0.4,1,1S13.6,11,13,11z"></path> </g><g> <path d="M16,15H6c-0.6,0-1-0.4-1-1s0.4-1,1-1h10c0.6,0,1,0.4,1,1S16.6,15,16,15z"></path> </g></svg>
                                                        <span className="settings-sub-menu-name">{t('dropdown_ctrl_settings.show_comments')}</span>
                                                    </div>
                                                    <div className="control-button-with-key-right-part">
                                                        <CtlSwitch on={!!diagramPrefs.show_comments} />
                                                    </div>
                                                </button>
                                            </div>
                                            <div className="control">
                                                <button
                                                    className={`control-button control-button-with-key view-trigger ${openSettingsSubMenu !== 'view' ? 'selected' : ''}`}
                                                    type="button"
                                                    onMouseEnter={closeViewFlyout}
                                                    onClick={() => toggleDiagramPref('show_scrollbars')}
                                                >
                                                    <div className="control-button-with-key-left-part">
                                                        <svg width="18" height="20" viewBox="0 0 24 24" fill="none" stroke="#f0f0f0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', marginLeft: '-1px', marginRight: '-3px' }}><rect x="3" y="4" width="18" height="16" rx="2"></rect><path d="M18 8v8"></path></svg>
                                                        <span className="settings-sub-menu-name">{t('dropdown_ctrl_settings.show_scrollbars')}</span>
                                                    </div>
                                                    <div className="control-button-with-key-right-part">
                                                        <CtlSwitch on={!!diagramPrefs.show_scrollbars} />
                                                    </div>
                                                </button>
                                            </div>
                                            <div className="control">
                                                <button
                                                    className={`control-button control-button-with-key view-trigger ${openSettingsSubMenu !== 'view' ? 'selected' : ''}`}
                                                    type="button"
                                                    onMouseEnter={closeViewFlyout}
                                                    onClick={() => toggleDiagramPref('show_undo_redo')}
                                                >
                                                    <div className="control-button-with-key-left-part">
                                                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '-2px', marginRight: '-5px', width: '19px' }}><path d="M8.5 5.5L4 10l4.5 4.5"></path><path d="M4 10h9a6 6 0 0 1 6 6v2"></path></svg>
                                                        <span className="settings-sub-menu-name">{t('dropdown_ctrl_settings.undo_redo_controls')}</span>
                                                    </div>
                                                    <div className="control-button-with-key-right-part">
                                                        <CtlSwitch on={!!diagramPrefs.show_undo_redo} />
                                                    </div>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="control-divider"></div>

                            {/* <div className="control">
                                <button
                                    className={`control-button control-button-with-key`}
                                    type="button"
                                    onClick={() => {
                                        dispatch(toggleBtnSettings());
                                        setTimeout(() => { dispatch(togglePricingPlanUpgrade('canvas')); }, 100)
                                    }}
                                >
                                    <div className="control-button-with-key-left-part">
                                        <svg width="15px" height="24" fill="#000000" viewBox="0 0 24 24" style={{ marginLeft: '-1px', marginRight: '-2px' }}><path d="M12,24C5.4,24,0,18.6,0,12S5.4,0,12,0s12,5.4,12,12S18.6,24,12,24z M12,2C6.5,2,2,6.5,2,12s4.5,10,10,10s10-4.5,10-10 S17.5,2,12,2z M13,18h-2V9.4l-3.3,3.3l-1.4-1.4L12,5.6l5.7,5.7l-1.4,1.4L13,9.4V18z" fill="currentColor" strokeWidth="0.3px" stroke="white"></path></svg>
                                        <span>{t('dropdown_ctrl_settings.upgrade_plan')}</span>
                                    </div>
                                </button>
                            </div> */}

                            <div className="control">
                                <button
                                    className={`control-button control-button-with-key language-trigger ${openSettingsSubMenu === 'language' ? 'selected' : ''}`}
                                    type="button"
                                    onMouseEnter={() => {
                                        if (openSettingsSubMenu === 'language') return;
                                        if (openSettingsSubMenu === 'view') setOpenSettingsSubMenu(null);
                                        cancelOpenSubMenuTimer();
                                        openSubMenuTimerRef.current = setTimeout(() => {
                                            openSubMenuTimerRef.current = null;
                                            setOpenSettingsSubMenu('language');
                                        }, 150);
                                    }}
                                    onMouseLeave={cancelOpenSubMenuTimer}
                                    onClick={() => {
                                        cancelOpenSubMenuTimer();
                                        setOpenSettingsSubMenu('language');
                                    }}
                                >
                                    <div className="control-button-with-key-left-part">
                                        <svg width="17px" height="20" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '-1px', marginRight: '-3px' }}><path d="M12 1C5.9 1 1 5.9 1 12s4.9 11 11 11 11-4.9 11-11S18.1 1 12 1Zm8 11c0 .7-.1 1.4-.3 2-.6-1.5-1.6-3.1-3-4.7l1.8-1.8c1 1.3 1.5 2.8 1.5 4.5ZM6.5 6.5c1.3 0 3.6.8 6 2.9l-3.2 3.2C7.1 9.8 6.5 7.5 6.5 6.5Zm8.1 5c2.3 2.7 2.9 5 2.9 6-1.3 0-3.6-.8-6-2.9l3.1-3.1Zm1.9-6.1-1.9 1.9c-1.6-1.4-3.2-2.4-4.7-3 .7-.2 1.3-.3 2-.3 1.8 0 3.3.5 4.6 1.4ZM4 12c0-.7.1-1.4.3-2 .6 1.5 1.6 3.1 3 4.7l-1.8 1.8C4.5 15.2 4 13.7 4 12Zm3.5 6.6 1.9-1.9c1.6 1.4 3.2 2.4 4.7 3-.7.2-1.3.3-2 .3-1.8 0-3.3-.5-4.6-1.4Z" fill="#e4e5e7" stroke="black" strokeWidth="0.8px"></path></svg>                                        {/* <span>{t('dropdown_ctrl_settings.keyboard_shortcuts')}</span> */}
                                        <span>{t('dropdown_ctrl_settings.language')}</span>
                                    </div>
                                    <div className="control-button-with-key-right-part">
                                        <svg className="setting-icon-arrow" width="16px" height="18px" fill="none" viewBox="0 -2 24 24" stroke="#8e94afb3" strokeWidth="2px" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" stroke="rgb(134, 145, 148)"></path></svg>
                                    </div>
                                </button>

                                {openSettingsSubMenu === 'language' && (
                                    <div className="settings-sub-menu settings-sub-menu-language">
                                        <div className="settings-sub-menu-list">
                                            {LANGUAGES.map((lng) => {
                                                const isActive = i18n.language === lng.code;
                                                return (
                                                    <div className="control" key={lng.code}>
                                                        <button
                                                            className={`control-button ${isActive ? 'selected' : ''}`}
                                                            type="button"
                                                            onClick={(e) => {
                                                                // The close below unmounts the menu before the document
                                                                // click handler runs; without stopPropagation its stale
                                                                // closure would toggle the menu back open.
                                                                e.stopPropagation();
                                                                dispatch(preferencesPatchRequest({ language: lng.code }));
                                                                i18n.changeLanguage(lng.code);
                                                                setOpenSettingsSubMenu(null);
                                                                dispatch(toggleBtnSettings());
                                                            }}
                                                        >
                                                            <span className="settings-sub-menu-name">{lng.native}</span>
                                                            {isActive && <OkCheckIcon />}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>


                        </React.Fragment>
                        : ''}
                </div>
            </div>
        );
    };

    // new diagram menu
    const getNewDiagramMenu = (isBtnNewDiagramEnabled) => {
        if (!isBtnNewDiagramEnabled) {
            return;
        }

        if (!newDiagramBtnRef.current) {
            return;
        }

        const rect = newDiagramBtnRef.current.getBoundingClientRect();
        let top = rect.bottom + window.scrollY + 1 - 0.5;
        let left = rect.left + window.scrollX - 5 - 85;

        if (isDesktop1350) {
            top = top - 2;
        }

        return (
            <div
                className={`settings-group new-diagram-group`}
                ref={newDiagramMenuRef}
                style={{ top: `${top}px`, left: `${left}px` }}
            >
                <div className="controls-group">
                    <div className="control">
                        <button
                            className={`control-button control-button-with-key t_btn_new_diagram`}
                            type="button"
                            onClick={() => {
                                dispatch(toggleBtnNewDiagram());
                                setTimeout(() => { dispatch(toggleDiagramCreateNew('canvas', diagramId)); }, 100)
                            }}
                        >
                            <div className="control-button-with-key-left-part">
                                <svg className="control-icon-new-diagram" fill="#000000" width="22px" height="23px" viewBox="0 0 24 24"><path d="M12,18c-0.6,0-1-0.4-1-1V7c0-0.6,0.4-1,1-1s1,0.4,1,1v10C13,17.6,12.6,18,12,18z" fill="currentColor" stroke="black" strokeWidth="0.1px"></path><path d="M17,13H7c-0.6,0-1-0.4-1-1s0.4-1,1-1h10c0.6,0,1,0.4,1,1S17.6,13,17,13z" fill="currentColor" stroke="black" strokeWidth="0.1px"></path></svg>
                                <span>{t('dropdown_ctrl_settings.new_project')}</span>
                            </div>
                            <div className="control-button-with-key-right-part">
                                <span className="control-button-with-key-right-part-text">{shortKeysMap?.CTRL}</span>
                                <span className="control-button-with-key-right-part-connector">+</span>
                                <span className="control-button-with-key-right-part-text text_shift">{shortKeysMap?.SHIFT}</span>
                                <span className="control-button-with-key-right-part-connector">+</span>
                                <span className="control-button-with-key-right-part-text">O</span>
                            </div>
                        </button>
                    </div>

                    {/* <div className="control control-m-1-3">
                        <button
                            className={`control-button`}
                            type="button"
                            onClick={() => {
                                dispatch(toggleBtnNewDiagram());
                                setTimeout(() => { dispatch(toggleDiagramCreateNew('canvas', diagramId)); }, 100)
                            }}
                        >
                            <svg className="control-icon-import" width="17px" height="18px" viewBox="0 0 24 24" fill="none"><g> <path d="M16 11L12 15M12 15L8 11M12 15V3M21 15V17C21 18.1046 20.1046 19 19 19H5C3.89543 19 3 18.1046 3 17V15" stroke="#000000" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"></path> </g></svg>
                            {t('dropdown_ctrl_settings.import_diagram')}
                        </button>
                    </div> */}

                    <div className="control">
                        <button
                            className={`control-button t_btn_new_diagram`}
                            type="button"
                            onClick={() => {
                                dispatch(toggleBtnNewDiagram());
                                setTimeout(() => { dispatch(toggleModalDiagramClone('canvas', diagramId)); }, 100)
                            }}
                        >
                            <svg className="control-icon-duplicate" fill="white" width="16px" height="16px" viewBox="0 0 32 32"><g><path stroke="white" strokeWidth="0.8px" fill="#e6e6e6" d="M27.2,8.22H23.78V5.42A3.42,3.42,0,0,0,20.36,2H5.42A3.42,3.42,0,0,0,2,5.42V20.36a3.42,3.42,0,0,0,3.42,3.42h2.8V27.2A2.81,2.81,0,0,0,11,30H27.2A2.81,2.81,0,0,0,30,27.2V11A2.81,2.81,0,0,0,27.2,8.22ZM5.42,21.91a1.55,1.55,0,0,1-1.55-1.55V5.42A1.54,1.54,0,0,1,5.42,3.87H20.36a1.55,1.55,0,0,1,1.55,1.55v2.8H11A2.81,2.81,0,0,0,8.22,11V21.91ZM28.13,27.2a.93.93,0,0,1-.93.93H11a.93.93,0,0,1-.93-.93V11a.93.93,0,0,1,.93-.93H27.2a.93.93,0,0,1,.93.93Z"></path></g></svg>
                            {t('dropdown_ctrl_settings.duplicate_diagram')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // export menu
    const getSqlSyntaxMenu = (isBtnSqlSyntaxEnabled) => {
        if (!isBtnSqlSyntaxEnabled) {
            return;
        }

        if (!sqlSyntaxBtnRef.current) {
            return;
        }


        const rect = sqlSyntaxBtnRef.current.getBoundingClientRect();

        const sqlSyntaxMenuHalfWidth = 71;
        const sqlSyntaxBtnHalfWidth = rect.width / 2;

        const left = rect.left + window.scrollX + sqlSyntaxBtnHalfWidth - sqlSyntaxMenuHalfWidth;

        return (
            <div
                className={`export-code-group`}
                ref={sqlSyntaxMenuRef}
                style={{ top: `${53}px`, left: `${left}px` }}
            >
                <div className="controls-group">
                    {/* <div className="control">
                        <button
                            className={`control-button hovered`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => {
                                dispatch(setSqlSyntax('basicsql'));
                            }}
                        >
                            Basic SQL
                        </button>
                    </div> */}

                    <div className="control">
                        <button
                            className={`control-button ${diagramDbEngine === 'mysql' ? 'selected' : ''}`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => {
                                dispatch(setSqlSyntax('mysql'));
                            }}
                        >
                            MySQL
                        </button>
                    </div>

                    <div className="control">
                        <button
                            className={`control-button ${diagramDbEngine === 'postgresql' ? 'selected' : ''}`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => {
                                dispatch(setSqlSyntax('postgresql'));
                            }}
                        >
                            PostgreSQL
                        </button>
                    </div>

                    <div className="control">
                        <button
                            className={`control-button ${diagramDbEngine === 'sqlserver' ? 'selected' : ''}`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => {
                                dispatch(setSqlSyntax('sqlserver'));
                            }}
                        >
                            SQL Server
                        </button>
                    </div>

                    <div className="control">
                        <button
                            className={`control-button ${diagramDbEngine === 'oracle' ? 'selected' : ''}`}
                            type="button"
                            title=""
                            aria-label="Color"
                            aria-pressed="false"
                            style={{ fontSize: '24px', fontWeight: 700 }}
                            onClick={() => {
                                dispatch(setSqlSyntax('oracle'));
                            }}
                        >
                            Oracle
                        </button>
                    </div>

                </div>
            </div>
        );
    };






    useEffect(() => {
        const handleDocumentClick = (event) => {
            // check if press any button exept 'left mouse button' then return nothing
            if (event.button !== 0) {
                return;
            }

            // check if 'settings menu enabled' and click outside of 'settings menu' and 'settings button'
            if (
                isBtnSettingsEnabled &&
                !settingsMenuRef.current?.contains(event.target) &&
                !settingsBtnRef.current?.contains(event.target)
            ) {
                dispatch(toggleBtnSettings());
            }

            // check if 'new diagram menu enabled' and click outside of 'new diagram menu' and 'new diagram button'
            if (
                isBtnNewDiagramEnabled &&
                !newDiagramMenuRef.current?.contains(event.target) &&
                !newDiagramBtnRef.current?.contains(event.target)
            ) {
                dispatch(toggleBtnNewDiagram());
            }

            // check if 'view modes menu enabled' and click outside of 'view modes menu' and 'view modes button'
            if (
                isBtnViewModesEnabled &&
                !viewModesMenuRef.current?.contains(event.target) &&
                !viewModesBtnRef.current?.contains(event.target)
            ) {
                dispatch(toggleBtnViewModes());
            }

            // check if 'grid style menu enabled' and click outside of 'grid style menu' and 'grid style button'
            if (
                isBtnGridStyleEnabled &&
                !gridStyleMenuRef.current?.contains(event.target) &&
                !gridStyleBtnRef.current?.contains(event.target)
            ) {
                dispatch(toggleBtnGridStyle());
            }

            // check if 'export menu enabled' and click outside of 'export menu' and 'export button'
            if (
                isBtnExportEnabled &&
                !exportMenuRef.current?.contains(event.target) &&
                !exportBtnRef.current?.contains(event.target) &&
                !exportCodeMenuRef.current?.contains(event.target)
            ) {
                dispatch(toggleBtnExport());
            }

            // check if 'sql syntax menu enabled' and click outside of 'sql syntax menu' and 'sql syntax button'
            if (
                isBtnSqlSyntaxEnabled &&
                !sqlSyntaxMenuRef.current?.contains(event.target) &&
                !sqlSyntaxBtnRef.current?.contains(event.target)
            ) {
                dispatch(toggleBtnSqlSyntax());
            }

        };

        document.addEventListener('click', handleDocumentClick);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [
        dispatch,
        isBtnSearchEnabled,
        isBtnViewModesEnabled,
        isBtnExportEnabled,
        isBtnSqlSyntaxEnabled,
        isBtnSettingsEnabled,
        isBtnNewDiagramEnabled,
        isBtnGridStyleEnabled,
    ]);



    // useEffect(() => {
    //     if (isBtnExportImageEnabled) {
    //         if (isTransparent) {
    //             setIsTransparent(false);
    //         }
    //     }
    // }, [isBtnExportImageEnabled]);


    return (
        // <div className="controls_top_left" style={{ top: `${isBtnPopupNewsEnabled ? 66 : 9 }px`}}>
        <div className={`controls_top_left ${topHidden ? 'focus-hidden' : ''}`} style={{ top: `${isBtnPopupNewsEnabled ? 58 : 11}px` }}>
            <div className="controls-row">

                {getSettingsMenu(isBtnSettingsEnabled)}

                {getNewDiagramMenu(isBtnNewDiagramEnabled)}

                {getViewModesMenu(isBtnViewModesEnabled)}
                {getGridStyleMenu(isBtnGridStyleEnabled)}

                {/* {getSqlSyntaxMenu(isBtnSqlSyntaxEnabled)} */}

                {getExportMenu(isBtnExportEnabled)}
                {/* {getExportImageMenu(isBtnExportImageEnabled)} */}
                <ExportImageMenu isBtnExportImageEnabled={isBtnExportImageEnabled} exportBtnRef={exportBtnRef} />
                {getExportCodeMenu(isBtnExportCodeEnabled)}

                <div className="controls-group large">

                    {/* <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button back-link`}
                                    type="button"
                                    // title="Left Panel | [B]"
                                    // aria-label="Toggle Left Panel"
                                    aria-pressed="false"
                                    onClick={() => handleClick()}
                                >
                                    <IconBack />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls tooltip-back-link"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>Back</span>
                                <Tooltip.Arrow className="tooltip-arrow" />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <div className="control-divider" style={{ marginLeft: '1px' }}></div> */}




                    <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    ref={settingsBtnRef}
                                    className={`control-button settings ${isBtnSettingsEnabled && 'selected'}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() =>
                                        dispatch(toggleBtnSettings({}))
                                    }
                                >
                                    ≡
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls tooltip-back-link wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>{t('t.ctrl_top_left.main_menu')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>


                    <div className="control-divider" style={{ marginLeft: '1px' }}></div>

                    {isDesktop ?
                        <React.Fragment>
                            <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                                <Tooltip.Trigger asChild>
                                    <div className="control">
                                        <button
                                            className="control-button button-logo logo"
                                            type="button"
                                            aria-pressed="false"
                                            onClick={() => handleClick()}
                                        >
                                            {/* <img
                                        className="img-logo"
                                        src="/logo_dark2.png"
                                        alt="logo"
                                    /> */}
                                            {/* <LogoIcon /> */}
                                            <span
                                                className="span-logo logo-text"
                                                aria-hidden="true"
                                            >dbdesign</span>
                                        </button>
                                    </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content
                                        className="tooltip-content tooltip-controls wo-shortcut"
                                        side="bottom"
                                        align="center"
                                        sideOffset={11}
                                    >
                                        <span>{t('t.ctrl_top_left.home')}</span>
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>

                            <div className="control-divider"></div>
                        </React.Fragment>
                        : ''}


                    <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button button-logo title ${isModalDiagramDetailsEnabled ? 'selected' : ''}`}
                                    type="button"
                                    // title="Title"
                                    // aria-label="Title"
                                    aria-pressed="false"
                                    onClick={() =>
                                        dispatch(toggleDiagramDetails('canvas', diagramId))
                                    }
                                >
                                    <span
                                        className="span-logo"
                                        aria-hidden="true"
                                    >
                                        {/* {window.devicePixelRatio || 0} */}
                                        {diagramTitle}
                                    </span>
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={11}
                            >
                                <span>{t('t.ctrl_top_left.name')}</span>
                                {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    {/* <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                        <Tooltip.Trigger asChild> */}
                    {!isArchived && <div className="control control-m-l-2">
                        <button
                            ref={sqlSyntaxBtnRef}
                            className={`control-button select_database ${(diagramDbEngine !== 'basicsql') ? 'selected' : ''}`}
                            type="button"
                            aria-pressed="false"
                            onClick={() => dispatch(toggleBtnSqlSyntax())}
                        >
                            {buildDbEngineName(diagramDbEngine, t)}
                        </button>
                    </div>}
                    {isArchived && (
                        <div className="control control-m-l-2">
                            <button
                                className={`control-button select_database archived`}
                                type="button"
                                aria-pressed="false"
                            >
                                Archived
                            </button>
                        </div>
                    )}

                    {/* </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>SQL Syntax & Functions</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root> */}

                    {/* <div className="control" style={{ marginLeft: '2px' }}>
                        <button
                            className={`control-button button_upgrade ${isModalDiagramDetailsEnabled ? 'selected' : ''}`}
                            type="button"
                            aria-pressed="false"
                            onClick={() => {
                                dispatch(togglePricingPlanUpgrade('canvas'));
                            }}
                        >
                            <span>{t('b.upgrade')}</span>
                        </button>
                    </div> */}
                </div>


                {isDesktop ?
                    <div className="controls-group">
                        <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                            <Tooltip.Trigger asChild>
                                <div className="control" style={{ marginRight: '1px' }}>
                                    <button
                                        disabled
                                        className={`control-button ${isBtnLeftPanelEnabled && 'selected'
                                            } disabled`}
                                        // className={`control-button ${isBtnLeftPanelEnabled && 'selected'}`}
                                        type="button"
                                        // title="Left Panel | [B]"
                                        // aria-label="Toggle Left Panel"
                                        aria-pressed="false"
                                        onClick={() =>
                                            dispatch(toggleBtnLeftPanel({}))
                                        }
                                    >
                                        {/* <LeftPanelOpenIcon /> */}
                                        {/* <LeftSidebarOpenedIcon /> */}
                                        {/* <LeftSidebarClosedIcon /> */}
                                        <EditorIcon />
                                    </button>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className="tooltip-content tooltip-controls"
                                    side="bottom"
                                    align="center"
                                    sideOffset={11}
                                >
                                    <span>{t('t.ctrl_top_left.editor')}</span>

                                    <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + B</span>
                                    {/* <span className="tooltip-shortcut">B</span> */}
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>

                        <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                            <Tooltip.Trigger asChild>
                                <div className="control control-search">
                                    <button
                                        className={`control-button ${isBtnSearchEnabled && 'selected'}`}
                                        type="button"
                                        aria-label={t('t.ctrl_top_left.search')}
                                        aria-pressed={isBtnSearchEnabled ? 'true' : 'false'}
                                        onClick={() => dispatch(toggleBtnSearch())}
                                    >
                                        <SearchIcon />
                                    </button>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className="tooltip-content tooltip-controls"
                                    side="bottom"
                                    align="center"
                                    sideOffset={11}
                                >
                                    <span>{t('t.ctrl_top_left.search')}</span>
                                    <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + K</span>
                                    {/* <span className="tooltip-shortcut">K</span> */}
                                    {/* <span className="tooltip-shortcut">F</span> */}
                                    {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>

                        <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                            <Tooltip.Trigger asChild>
                                <div className="control" style={{ marginRight: '2px' }}>
                                    <button
                                        ref={exportBtnRef}
                                        className={`control-button ${(isBtnExportEnabled ||
                                            isBtnExportImageEnabled ||
                                            isBtnExportCodeEnabled) &&
                                            'selected'
                                            }`}
                                        type="button"
                                        // title="Export | [E]"
                                        // title="Export By Image / SQL Code"
                                        // aria-label="Export"
                                        aria-pressed="false"
                                        onClick={() => dispatch(toggleBtnExport())}
                                    >
                                        <ExportIcon />
                                    </button>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className="tooltip-content tooltip-controls"
                                    side="bottom"
                                    align="center"
                                    sideOffset={11}
                                >
                                    <span>{t('t.ctrl_top_left.export')}</span>
                                    {/* <span className="tooltip-shortcut">{shortKeysMap?.CTRL}</span> */}
                                    <span className="tooltip-shortcut">E</span>
                                    {/* <Tooltip.Arrow className="tooltip-arrow" /> */}
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>

                        <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                            <Tooltip.Trigger asChild>
                                <div className="control" style={{ marginLeft: '2px' }}>
                                    <button
                                        // ref={exportBtnRef}
                                        // className={`control-button ${(isBtnExportEnabled ||
                                        //     isBtnExportImageEnabled ||
                                        //     isBtnExportCodeEnabled) &&
                                        //     'selected'
                                        //     }`}
                                        className={`control-button disabled`}
                                        disabled
                                        type="button"
                                        aria-pressed="false"
                                    // onClick={() => dispatch(toggleBtnExport())}
                                    >
                                        <HistoryVersionsIcon />
                                    </button>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className="tooltip-content tooltip-controls"
                                    side="bottom"
                                    align="center"
                                    sideOffset={11}
                                >
                                    <span>{t('t.ctrl_top_left.history')}</span>
                                    <span className="tooltip-shortcut">H</span>
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>


                        <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                            <Tooltip.Trigger asChild>
                                <div className="control" style={{ marginLeft: '-1px' }}>
                                    <button
                                        ref={newDiagramBtnRef}
                                        className={`control-button ${isBtnNewDiagramEnabled && 'selected'}`}
                                        type="button"
                                        aria-pressed="false"
                                        onClick={() => dispatch(toggleBtnNewDiagram())}
                                    >
                                        <svg className="control-icon-new-diagram" fill="#000000" width="24px" height="24px" viewBox="0 0 24 24"><path d="M12,18c-0.6,0-1-0.4-1-1V7c0-0.6,0.4-1,1-1s1,0.4,1,1v10C13,17.6,12.6,18,12,18z" fill="currentColor" stroke="black" strokeWidth="0.2px"></path><path d="M17,13H7c-0.6,0-1-0.4-1-1s0.4-1,1-1h10c0.6,0,1,0.4,1,1S17.6,13,17,13z" fill="currentColor" stroke="black" strokeWidth="0.2px"></path></svg>
                                    </button>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className="tooltip-content tooltip-controls wo-shortcut"
                                    side="bottom"
                                    align="center"
                                    sideOffset={11}
                                >
                                    <span>{t('t.ctrl_top_left.new_diagram')}</span>
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>

                    </div>
                    : ''}

                <div className="controls-group">

                    <Tooltip.Root ignoreNonKeyboardFocus delayDuration={500} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control control-m-r-3">
                                <button
                                    ref={gridStyleBtnRef}
                                    // className={`control-button ${(isBtnGridStyleEnabled || gridStyle) ? 'selected' : ''}`}
                                    className={`control-button ${(isBtnGridStyleEnabled) ? 'selected' : ''}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() =>
                                        dispatch(toggleBtnGridStyle())
                                    }
                                >
                                    <svg width="19px" height="19px" viewBox="1 0 48 48" fill="#000000" style={{ marginTop: '1px' }}><g> <path d="M44,26a2,2,0,0,0,0-4H38V14h6a2,2,0,0,0,0-4H38V4a2,2,0,0,0-4,0v6H26V4a2,2,0,0,0-4,0v6H14V4a2,2,0,0,0-4,0v6H4a2,2,0,0,0,0,4h6v8H4a2,2,0,0,0,0,4h6v8H4a2,2,0,0,0,0,4h6v6a2,2,0,0,0,4,0V38h8v6a2,2,0,0,0,4,0V38h8v6a2,2,0,0,0,4,0V38h6a2,2,0,0,0,0-4H38V26ZM34,14v8H26V14ZM14,14h8v8H14Zm0,20V26h8v8Zm20,0H26V26h8Z" fill="#f3f3f3" stroke="black" strokeWidth="1.2px"></path></g></svg>
                                    {/* <GridIconNew /> */}
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={11}
                            >
                                <span>{t('t.ctrl_top_left.grid')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>


                    <Tooltip.Root ignoreNonKeyboardFocus delayDuration={500} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control control-m-l-2 control-m-r-3">
                                <button
                                    ref={viewModesBtnRef}
                                    className={`control-button ${(isBtnViewModesEnabled || viewMode !== 'full') && 'selected'}`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() =>
                                        dispatch(toggleBtnViewModes())
                                    }
                                >
                                    <ViewModeIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls wo-shortcut"
                                side="bottom"
                                align="center"
                                sideOffset={11}
                            >
                                <span>{t('t.ctrl_top_left.view_mode')}</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>

                    <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            {/* <div className="control control-m-l-2 control-m-r-1"> */}
                            <div className="control control-m-l-2">
                                <button
                                    className={`control-button`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => dispatch(updateScaleToFit())}
                                >
                                    <FitToZoomIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="bottom"
                                align="center"
                                sideOffset={11}
                            >
                                <span>{t('t.ctrl_top_left.zoom_to_fit')}</span>

                                <span className="tooltip-shortcut">{shortKeysMap?.CTRL} + Enter</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>


                </div>

                {/* <div className="controls-group">
                    <div className="control">
                        <button
                            className={`control-button disabled`}
                            type="button"
                            title="Validation + Validation Tooltip like 'Warnings (2)' with report by click on it"
                            aria-label="Enable Validation"
                            aria-pressed="false"
                        >
                            <AutoLayoutIcon />
                        </button>
                    </div>

                    <Tooltip.Root
                        delayDuration={300}
                        disableHoverableContent
                    >
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    disabled
                                    className={`control-button ${isBtnRedGreenViewEnabled &&
                                        'selected'
                                        } disabled`}
                                    type="button"
                                    // title="Red/Green View | [R]"
                                    // aria-label="4-Change View (PR)"
                                    aria-pressed="false"
                                    onClick={() =>
                                        dispatch(toggleBtnRedGreenView())
                                    }
                                >
                                    <ChangeRequestIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="bottom"
                                align="center"
                                sideOffset={10}
                            >
                                <span>Red / Green View</span>
                                <span className="tooltip-shortcut">R</span>

                                <Tooltip.Arrow className="tooltip-arrow" />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div> */}

                {/* <Tooltip.Root ignoreNonKeyboardFocus delayDuration={300} disableHoverableContent>
                        <Tooltip.Trigger asChild>
                            <div className="control">
                                <button
                                    className={`control-button`}
                                    type="button"
                                    aria-pressed="false"
                                    onClick={() => dispatch(updateScaleToFit())}
                                >
                                    <AutoLayoutIcon />
                                </button>
                            </div>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content
                                className="tooltip-content tooltip-controls"
                                side="bottom"
                                align="center"
                                sideOffset={11}
                            >
                                <span>Auto Layout</span>
                                <Tooltip.Arrow className="tooltip-arrow" />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root> */}
            </div>
        </div>
    );
};

export default ControlsTopLeft;
